import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { User } from '../../types';

export const DAILY_TOKEN_LIMIT = 50000;
export const MONTHLY_TOKEN_LIMIT = 1000000;

export async function checkAndIncrementUsage(userId: string, tokens: number): Promise<void> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    const now = new Date();
    const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const currentMonth = today.substring(0, 7); // YYYY-MM

    let usage = {
        daily: { date: today, count: 0 },
        monthly: { month: currentMonth, count: 0 }
    };

    if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        if (userData.usage) {
            usage = { ...userData.usage };

            // Reset daily if needed
            if (usage.daily.date !== today) {
                usage.daily = { date: today, count: 0 };
            }

            // Reset monthly if needed
            if (usage.monthly.month !== currentMonth) {
                usage.monthly = { month: currentMonth, count: 0 };
            }
        }
    }

    // Check limits
    if (usage.daily.count + tokens > DAILY_TOKEN_LIMIT) {
        throw new Error(`Limite diário de tokens atingido (${usage.daily.count}/${DAILY_TOKEN_LIMIT}). Tente novamente amanhã.`);
    }

    if (usage.monthly.count + tokens > MONTHLY_TOKEN_LIMIT) {
        throw new Error(`Limite mensal de tokens atingido (${usage.monthly.count}/${MONTHLY_TOKEN_LIMIT}). Tente novamente no próximo mês.`);
    }

    // Update usage
    usage.daily.count += tokens;
    usage.monthly.count += tokens;

    // Save to Firestore
    if (userDoc.exists()) {
        await updateDoc(userRef, { usage });
    } else {
        await setDoc(userRef, { usage }, { merge: true });
    }
}

export async function checkUsage(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return;

    const userData = userDoc.data() as User;
    if (!userData.usage) return;

    const usage = userData.usage;
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentMonth = today.substring(0, 7);

    // If dates match, check limits
    if (usage.daily.date === today && usage.daily.count >= DAILY_TOKEN_LIMIT) {
        throw new Error(`Limite diário de tokens atingido (${usage.daily.count}/${DAILY_TOKEN_LIMIT}). Tente novamente amanhã.`);
    }

    if (usage.monthly.month === currentMonth && usage.monthly.count >= MONTHLY_TOKEN_LIMIT) {
        throw new Error(`Limite mensal de tokens atingido (${usage.monthly.count}/${MONTHLY_TOKEN_LIMIT}). Tente novamente no próximo mês.`);
    }
}

export async function getUserUsage(userId: string): Promise<User['usage'] | null> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
        return (userDoc.data() as User).usage || null;
    }
    return null;
}
